import type { ColumnConfiguration } from 'apex-grid';
import type { GridFeatureModule, GridHost, RowTransformer } from 'apex-grid/internal';
import type { ReactiveController } from 'lit';
import { type AggregationConfig, type AggregationFn, computeAggregations } from './aggregation.js';

export const PIVOT_MODULE_ID = 'pivot';

interface PivotColumnSpec {
  /** Synthetic column key the pivot row carries its computed value under. */
  readonly key: string;
  /** Stringified distinct value of the column-dimension field. */
  readonly pivotValue: string;
  /** Measure (source field) being aggregated. */
  readonly measure: string;
  /** Aggregation function. */
  readonly fn: AggregationFn;
  readonly headerText: string;
}

function stringify(value: unknown): string {
  return value === null || value === undefined || value === '' ? '(blank)' : String(value);
}

function capitalize(value: string): string {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

/**
 * Enterprise feature: pivoting. Turns the distinct values of a column-dimension
 * field (`on`) into columns, groups rows by one or more row-dimension fields
 * (`rows`), and fills each cell with an aggregate (`values`) of the matching
 * leaves — a cross-tab pivot table.
 *
 * Implemented purely enterprise-side: it implements {@link RowTransformer} to
 * synthesize one pivot row per row-dimension combination (each carrying the
 * computed aggregates under generated synthetic column keys), and exposes
 * {@link computeColumns} so the grid can swap in the generated columns. The
 * existing cell renderer reads `data[column.key]` — which is the precomputed
 * aggregate — so no core change is needed. Aggregates reuse
 * {@link computeAggregations} over the (filtered) leaves.
 */
export class PivotController<T extends object> implements ReactiveController, RowTransformer<T> {
  /** Row-dimension fields (one leading column each). */
  public rows: string[] = [];
  /** Column-dimension field whose distinct values become columns. */
  public on = '';
  /** Measures aggregated into each pivot cell, e.g. `{ amount: ['sum'] }`. */
  public values: AggregationConfig = {};

  #specs: PivotColumnSpec[] = [];

  constructor(private host: GridHost<T>) {
    host.addController(this);
  }

  public hostConnected(): void {}

  /** Whether pivoting is fully configured and should transform the view. */
  public get active(): boolean {
    return this.on !== '' && this.rows.length > 0 && this.#valueSpecCount() > 0;
  }

  #valueSpecCount(): number {
    return Object.values(this.values).reduce((total, fns) => total + fns.length, 0);
  }

  /**
   * Build the pivot column set from `data`: one leading column per row-dimension
   * field, then one column per (distinct column-dimension value × measure × fn).
   * Stores the specs so {@link processRows} fills matching cells.
   */
  public computeColumns(data: ReadonlyArray<T>): ColumnConfiguration<T>[] {
    const distinct = [
      ...new Set(data.map((row) => stringify((row as Record<string, unknown>)[this.on]))),
    ].sort();

    const single = this.#valueSpecCount() === 1;
    const blankLabel = this.host.localize('pivot.blank');
    this.#specs = [];
    for (const pivotValue of distinct) {
      // Localize the blank placeholder for display; the stable column key keeps the raw token.
      const display = pivotValue === stringify('') ? blankLabel : pivotValue;
      for (const [measure, fns] of Object.entries(this.values)) {
        for (const fn of fns) {
          this.#specs.push({
            key: `pivot::${pivotValue}::${measure}::${fn}`,
            pivotValue,
            measure,
            fn,
            headerText: single ? display : `${display} · ${measure} ${fn}`,
          });
        }
      }
    }

    const rowCols = this.rows.map(
      (field) =>
        ({ key: field, headerText: capitalize(field) }) as unknown as ColumnConfiguration<T>
    );
    const valueCols = this.#specs.map(
      (spec) =>
        ({
          key: spec.key,
          headerText: spec.headerText,
          type: 'number',
        }) as unknown as ColumnConfiguration<T>
    );
    return [...rowCols, ...valueCols];
  }

  public processRows(rows: ReadonlyArray<T>): T[] {
    if (!this.active) return rows as T[];

    // Partition by the row-dimension combination, preserving first-seen order.
    const combos = new Map<string, T[]>();
    for (const row of rows) {
      const comboKey = this.rows
        .map((field) => stringify((row as Record<string, unknown>)[field]))
        .join(' ');
      const bucket = combos.get(comboKey);
      if (bucket) {
        bucket.push(row);
      } else {
        combos.set(comboKey, [row]);
      }
    }

    const out: T[] = [];
    for (const leaves of combos.values()) {
      const pivotRow: Record<string, unknown> = {};
      for (const field of this.rows) {
        pivotRow[field] = (leaves[0] as Record<string, unknown>)[field];
      }
      for (const spec of this.#specs) {
        const subset = leaves.filter(
          (row) => stringify((row as Record<string, unknown>)[this.on]) === spec.pivotValue
        );
        const agg = computeAggregations(subset, { [spec.measure]: [spec.fn] });
        pivotRow[spec.key] = agg[spec.measure]?.[spec.fn] ?? 0;
      }
      out.push(pivotRow as unknown as T);
    }
    return out;
  }
}

/** Feature module registered on the enterprise grid. */
export const pivotModule: GridFeatureModule = {
  id: PIVOT_MODULE_ID,
  create: (host) => new PivotController(host),
};
